const ROW_TYPE = "row_type";
const SUMMARY_ID = "summary_id";
const SUMMARY_REFERENCE_ID = "summary_reference_id";
const ID = "id";
const SUMMARY_ROW_TYPE = 0;
const SUBLEG_ROW_TYPE = 2;
const LEG_ROW_TYPE = 1;

export function rowTypeValue(row) {
  const raw = row?.[ROW_TYPE];
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function isSublegRow(row) {
  return rowTypeValue(row) === SUBLEG_ROW_TYPE;
}

function summaryIdFromRow(row) {
  return row?.[SUMMARY_ID] ?? row?.[SUMMARY_REFERENCE_ID];
}

function rowMatchesSummary(row, summaryId) {
  if (summaryId == null || !row) return false;
  return String(summaryIdFromRow(row)) === String(summaryId);
}

export function rowIdFromRow(row) {
  const id = row?.[ID];
  return id == null || id === "" ? null : id;
}

/**
 * Path param for GET …/calculation-formulas — always the option summary row id,
 * never a leg/subleg row id when the user opened the menu from a child row.
 * @param {object | null | undefined} row
 */
export function resolveSummaryIdForFormulas(row) {
  if (!row) return null;
  const fromFields = summaryIdFromRow(row);
  if (rowTypeValue(row) === SUMMARY_ROW_TYPE) {
    return fromFields ?? rowIdFromRow(row);
  }
  return fromFields;
}

/**
 * Flat Pricer `rows` payload (same shape as `pricerTablesData`).
 * @param {object[] | null | undefined} flatRows
 * @param {number | string} summaryId
 */
export function findSublegsForLeg(flatRows, summaryId, legId) {
  if (summaryId == null || legId == null || !Array.isArray(flatRows)) {
    return [];
  }
  return flatRows.filter(
    (row) =>
      rowMatchesSummary(row, summaryId) &&
      isSublegRow(row) &&
      String(row.leg_id) === String(legId)
  );
}

export function findSublegCandidatesForSummary(flatRows, summaryId) {
  if (summaryId == null || !Array.isArray(flatRows)) return [];

  const forSummary = flatRows.filter((row) => rowMatchesSummary(row, summaryId));
  return forSummary.filter(isSublegRow);
}

/**
 * @param {import('ag-grid-community').GridApi | undefined} gridApi
 * @param {number | string} summaryId
 */
export function findSublegCandidatesFromGrid(gridApi, summaryId) {
  if (summaryId == null || !gridApi) return [];

  const found = [];
  const visit = (node) => {
    const data = node?.data ?? (node?.group ? node?.aggData : null);
    if (!data || !rowMatchesSummary(data, summaryId)) return;
    if (isSublegRow(data)) {
      found.push(data);
    }
  };

  if (typeof gridApi.forEachNodeAfterFilterAndSort === "function") {
    gridApi.forEachNodeAfterFilterAndSort(visit);
  } else {
    gridApi.forEachNode(visit);
  }

  return found;
}

/**
 * Optional `pricing_row_id` for the first request when the grid exposes a row
 * (subleg, or leg when sublegs are hidden). Omit when unknown — backend picks default.
 * @param {import('ag-grid-community').GridApi | undefined} gridApi
 * @param {number | string | null | undefined} summaryId
 * @param {object | null | undefined} rowEditingData
 * @param {object[] | null | undefined} [flatRows]
 * @returns {number | string | null}
 */
export function resolveInitialPricingRowIdForFormulas(
  gridApi,
  summaryId,
  rowEditingData,
  flatRows
) {
  if (summaryId == null) return null;

  if (rowEditingData && rowMatchesSummary(rowEditingData, summaryId)) {
    if (isSublegRow(rowEditingData)) {
      return rowIdFromRow(rowEditingData);
    }
    if (rowTypeValue(rowEditingData) === LEG_ROW_TYPE) {
      const legId = rowIdFromRow(rowEditingData);
      const fromFlat = findSublegsForLeg(flatRows, summaryId, legId);
      if (fromFlat.length) return rowIdFromRow(fromFlat[0]);
      const fromGrid = findSublegCandidatesFromGrid(gridApi, summaryId).filter(
        (row) => String(row.leg_id) === String(legId)
      );
      if (fromGrid.length) return rowIdFromRow(fromGrid[0]);
      return legId;
    }
  }

  const fromGrid = findSublegCandidatesFromGrid(gridApi, summaryId);
  const fromFlat = findSublegCandidatesForSummary(flatRows, summaryId);
  const candidates = fromGrid.length ? fromGrid : fromFlat;
  return candidates.length ? rowIdFromRow(candidates[0]) : null;
}

/** @deprecated Use resolveInitialPricingRowIdForFormulas */
export function resolveInitialSublegIdForFormulas(
  gridApi,
  summaryId,
  rowEditingData,
  flatRows
) {
  return resolveInitialPricingRowIdForFormulas(
    gridApi,
    summaryId,
    rowEditingData,
    flatRows
  );
}

/**
 * Laravel `{ data: { … } }` and legacy key names → spec shape.
 * @param {unknown} raw
 */
export function normalizeCalculationFormulasPayload(raw) {
  if (!raw || typeof raw !== "object") return null;

  let body = raw;
  if (
    body.data &&
    typeof body.data === "object" &&
    !Array.isArray(body.calculation_steps) &&
    !Array.isArray(body.calculationSteps)
  ) {
    body = body.data;
  }

  const sourceMethods =
    body.source_methods ?? body.sourceMethods ?? [];
  const calculationSteps =
    body.calculation_steps ?? body.calculationSteps ?? [];

  const pricingTargets =
    body.pricing_targets ?? body.pricingTargets ?? body.sublegs ?? [];

  return {
    ...body,
    source_methods: Array.isArray(sourceMethods) ? sourceMethods : [],
    calculation_steps: Array.isArray(calculationSteps) ? calculationSteps : [],
    pricing_targets: Array.isArray(pricingTargets) ? pricingTargets : [],
    sublegs: Array.isArray(body.sublegs) ? body.sublegs : [],
    selected:
      body.selected && typeof body.selected === "object" ? body.selected : {},
    stored_row_values:
      body.stored_row_values ?? body.storedRowValues ?? {},
    calculation_model:
      body.calculation_model ?? body.calculationModel ?? null,
  };
}

/**
 * @param {{ label?: string | null, tenor?: string | null, strike?: string | null, is_call?: boolean }} target
 */
export function formatPricingTargetLabel(target) {
  if (target?.label) return String(target.label);
  return formatCalculationFormulasSublegLabel(target);
}

/**
 * @param {{ id: number, tenor?: string | null, strike?: string | null, is_call?: boolean }} subleg
 */
export function formatCalculationFormulasSublegLabel(subleg) {
  const parts = [];
  if (subleg.tenor) parts.push(String(subleg.tenor));
  if (subleg.strike != null && subleg.strike !== "") {
    parts.push(String(subleg.strike));
  }
  parts.push(subleg.is_call ? "Call" : "Put");
  return parts.join(" · ");
}

/**
 * @param {Record<string, string | number | null> | undefined} stored
 */
export function formatStoredRowValuesLines(stored) {
  if (!stored || typeof stored !== "object") return [];
  return Object.entries(stored).map(([key, value]) => ({
    key,
    label: key.replace(/_/g, " "),
    value: value == null || value === "" ? "—" : String(value),
  }));
}
